import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SPORTS, TEST_PRESETS } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const NUM_FIELDS = ["height_in", "weight_lb", "age"];
const blank = { name: "", sport: "soccer", position: "Forward", team: "", height_in: "", weight_lb: "", age: "", jersey_number: "",
  hometown: "", photo_url: "", season: "2025", stats: {}, testing: [], manual_override: true };

const toForm = (p) => (p ? { ...blank, ...p, ...Object.fromEntries(NUM_FIELDS.map((k) => [k, p[k] ?? ""])),
  stats: Object.fromEntries(Object.entries(p.stats || {}).map(([k, v]) => [k, String(v)])),
  testing: (p.testing || []).map((t) => ({ ...t, value: String(t.value), date: t.date || "" })) } : blank);

const toPayload = (f) => ({
  name: f.name, sport: f.sport, position: f.position, team: f.team, season: f.season, manual_override: f.manual_override,
  jersey_number: f.jersey_number || null, hometown: f.hometown || null, photo_url: f.photo_url || null,
  bats_throws: f.bats_throws || null, high_school: f.high_school || null, previous_school: f.previous_school || null,
  ...Object.fromEntries(NUM_FIELDS.map((k) => [k, f[k] === "" ? null : parseInt(f[k], 10)])),
  stats: Object.fromEntries(Object.entries(f.stats).filter(([, v]) => v !== "").map(([k, v]) => [k, parseFloat(v)])),
  testing: f.testing.filter((t) => t.test && t.value !== "").map((t) => ({ ...t, value: parseFloat(t.value), date: t.date || null })),
});

const L = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="eyebrow mb-1.5 block">{label}</span>{children}</label>
);
const inputCls = "border-slate-800 bg-[#0B0F19]";

export const PlayerFormDialog = ({ player, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(() => toForm(player));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const setStat = (k) => (e) => setF((s) => ({ ...s, stats: { ...s.stats, [k]: e.target.value } }));
  const setTest = (i, k, v) => setF((s) => ({ ...s, testing: s.testing.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const sport = SPORTS[f.sport];

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = player ? await api.put(`/admin/players/${player.id}`, toPayload(f)) : await api.post("/admin/players", toPayload(f));
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/players/${data.id}/photo`, fd);
      }
      toast.success(`${data.name} saved`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="player-form-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto border-slate-800 bg-[#131927]">
        <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase">{player ? `Edit ${player.name}` : "New player"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-8">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <L label="Name" className="col-span-2"><Input data-testid="form-name-input" required value={f.name} onChange={set("name")} className={inputCls} /></L>
            <L label="Sport">
              <Select value={f.sport} onValueChange={(v) => setF((s) => ({ ...s, sport: v, position: SPORTS[v].positions[0] || "" }))}>
                <SelectTrigger data-testid="form-sport-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SPORTS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </L>
            <L label="Position">
              <Select value={f.position} onValueChange={set("position")}>
                <SelectTrigger data-testid="form-position-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>{[...new Set([...sport.positions, f.position].filter(Boolean))].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </L>
            <L label="Team" className="col-span-2"><Input data-testid="form-team-input" value={f.team} onChange={set("team")} className={inputCls} /></L>
            <L label="Height (in)"><Input data-testid="form-height-input" type="number" value={f.height_in} onChange={set("height_in")} className={inputCls} /></L>
            <L label="Weight (lb)"><Input data-testid="form-weight-input" type="number" value={f.weight_lb} onChange={set("weight_lb")} className={inputCls} /></L>
            <L label="Age"><Input data-testid="form-age-input" type="number" value={f.age} onChange={set("age")} className={inputCls} /></L>
            <L label="Jersey #"><Input data-testid="form-jersey-input" value={f.jersey_number || ""} onChange={set("jersey_number")} className={inputCls} /></L>
            <L label="Hometown"><Input data-testid="form-hometown-input" value={f.hometown || ""} onChange={set("hometown")} className={inputCls} /></L>
            {sport.rosterOnly && (<>
              {f.sport === "softball" && <L label="Bats/Throws"><Input data-testid="form-bats-throws-input" value={f.bats_throws || ""} onChange={set("bats_throws")} placeholder="R/R" className={inputCls} /></L>}
              <L label="High school"><Input data-testid="form-high-school-input" value={f.high_school || ""} onChange={set("high_school")} className={inputCls} /></L>
              <L label="Previous school"><Input data-testid="form-previous-school-input" value={f.previous_school || ""} onChange={set("previous_school")} className={inputCls} /></L>
            </>)}
            <L label="Season"><Input data-testid="form-season-input" value={f.season} onChange={set("season")} className={inputCls} /></L>
            <L label="Photo URL" className="col-span-2"><Input data-testid="form-photo-url-input" value={f.photo_url || ""} onChange={set("photo_url")} placeholder="https://" className={inputCls} /></L>
            <L label="Or upload photo"><Input data-testid="form-photo-file-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className={inputCls} /></L>
          </div>

          <div>
            <h3 className="mb-3 font-display text-xl font-extrabold uppercase">Season stats</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {sport.stats.map((s) => (
                <L key={s.key} label={s.label}><Input data-testid={`form-stat-${s.key}`} type="number" step="any" value={f.stats[s.key] ?? ""} onChange={setStat(s.key)} className={inputCls} /></L>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-xl font-extrabold uppercase">Athletic testing</h3>
              <button type="button" data-testid="form-add-test-button" onClick={() => setF((s) => ({ ...s, testing: [...s.testing, { ...TEST_PRESETS[0], value: "", date: "" }] }))}
                className="inline-flex items-center gap-1 font-data text-[11px] uppercase tracking-wider text-[#FF2D78] hover:text-white"><Plus className="h-3.5 w-3.5" /> Add result</button>
            </div>
            <div className="space-y-2">
              {f.testing.map((t, i) => (
                <div key={i} data-testid={`form-test-row-${i}`} className="grid grid-cols-[1fr_90px_60px_auto] gap-2 sm:grid-cols-[1fr_100px_70px_150px_auto]">
                  <Input list="test-presets" value={t.test} onChange={(e) => { const pre = TEST_PRESETS.find((x) => x.test === e.target.value); setTest(i, "test", e.target.value); pre && setTest(i, "unit", pre.unit); }} placeholder="Test" className={inputCls} data-testid={`form-test-name-${i}`} />
                  <Input type="number" step="any" value={t.value} onChange={(e) => setTest(i, "value", e.target.value)} placeholder="Value" className={inputCls} data-testid={`form-test-value-${i}`} />
                  <Input value={t.unit} onChange={(e) => setTest(i, "unit", e.target.value)} placeholder="Unit" className={inputCls} data-testid={`form-test-unit-${i}`} />
                  <Input type="date" value={t.date} onChange={(e) => setTest(i, "date", e.target.value)} className={`${inputCls} hidden sm:block`} data-testid={`form-test-date-${i}`} />
                  <button type="button" onClick={() => setF((s) => ({ ...s, testing: s.testing.filter((_, j) => j !== i) }))} className="rounded-md p-2 text-slate-500 hover:text-red-400" data-testid={`form-test-remove-${i}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
              {!f.testing.length && <p className="text-sm text-slate-500">No testing results yet.</p>}
              <datalist id="test-presets">{TEST_PRESETS.map((p) => <option key={p.test} value={p.test} />)}</datalist>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-[#0B0F19] p-4">
            <div>
              <div className="text-sm font-semibold">Protect from FanFlare overwrite</div>
              <div className="text-xs text-slate-500">When on, weekly syncs will not replace this player's data.</div>
            </div>
            <Switch data-testid="form-manual-override-switch" checked={f.manual_override} onCheckedChange={set("manual_override")} />
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="form-cancel-button" className="rounded-full border border-slate-700 px-5 py-2.5 text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="form-save-button" className="inline-flex items-center gap-2 rounded-full bg-[#FF2D78] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#E01F66] disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save player
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
